import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { convertGlobalFiltersToQuery } from '../domain/Surveys/Responses/Filters/Query/GlobalFilterQuery/convertGlobalFiltersToQuery';
import { convertSearchParamsToGlobalFilters } from '../domain/Surveys/Responses/Filters/Query/GlobalFilterQuery/convertSearchParamsToGlobalFilters';
import { convertQueryToLocalFilters } from '../domain/Surveys/Responses/Filters/Query/LocalFilterQuery/convertQueryToLocalFilters';
import { convertLocalFiltersToQuery } from '../domain/Surveys/Responses/Filters/Query/LocalFilterQuery/convertLocalFiltersToQuery';
import { mergeLocalFilters } from '../domain/Surveys/Responses/Filters/Query/LocalFilterQuery/mergeLocalFilters';
import { mergeGlobalFilters } from '../domain/Surveys/Responses/Filters/Query/GlobalFilterQuery/mergeGlobalFilters';
import {
  defaultGlobalFilters,
  defaultLocalFilters,
  useSurveyViewStore,
} from '../domain/Surveys/surveyViewStore';

function updateParams(
  params: URLSearchParams,
  query: Record<string, string[] | string | undefined>,
): URLSearchParams {
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) {
      params.delete(key);
      return;
    }
    if (Array.isArray(value)) {
      const [first, ...rest] = value;
      params.set(key, first);
      rest.forEach((p) => params.append(key, p));
      return;
    }

    params.set(key, value);
  });
  return params;
}

export const useSurveyFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const store = useSurveyViewStore();
  const { setLocalFilters, setGlobalFilters } = store;

  useEffect(() => {
    setGlobalFilters(
      mergeGlobalFilters(defaultGlobalFilters, convertSearchParamsToGlobalFilters(searchParams)),
    );
    setLocalFilters(
      mergeLocalFilters(defaultLocalFilters, convertQueryToLocalFilters(searchParams)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrappedSetLocalFilters = useCallback(
    (...params: Parameters<typeof setLocalFilters>) => {
      const [newFilters] = params;
      setSearchParams(updateParams(searchParams, convertLocalFiltersToQuery(newFilters)));
      setLocalFilters(...params);
    },
    [setLocalFilters, setSearchParams, searchParams],
  );

  const wrappedSetGlobalFilters = useCallback(
    (...params: Parameters<typeof setGlobalFilters>) => {
      const [newGlobalFilters] = params;
      setSearchParams(updateParams(searchParams, convertGlobalFiltersToQuery(newGlobalFilters)));
      setGlobalFilters(...params);
    },
    [setGlobalFilters, searchParams, setSearchParams],
  );

  const wrappedStore = useMemo(() => {
    return {
      ...store,
      setLocalFilters: wrappedSetLocalFilters,
      setGlobalFilters: wrappedSetGlobalFilters,
    };
  }, [store, wrappedSetLocalFilters, wrappedSetGlobalFilters]);

  return wrappedStore;
};
